import { useEffect, useState } from 'react';

interface QrCodeProps {
  value: string;
  /** Rendered size in px; the SVG itself is resolution-independent. */
  size?: number;
  label: string;
}

/** The quiet zone the spec asks for. Scanners get unreliable without it. */
const MARGIN = 4;

/**
 * A QR code drawn as one SVG path.
 *
 * Deliberately dark-on-white rather than pip-green-on-black: a phone camera
 * reads a correctly polarised code first time, and an inverted one is a
 * coin toss across scanner apps. This is the one place in the app that
 * ignores the theme, because it is not really UI — it is a machine-readable
 * label that happens to be on screen.
 */
export function QrCode({ value, size = 184, label }: QrCodeProps) {
  const [code, setCode] = useState<{ d: string; count: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Loaded on demand. The encoder is ~50 KB and only the pairing screen ever
    // needs it, so Vite splits it out rather than charging every visitor for it.
    void import('qrcode-generator')
      .then((mod) => {
        const factory = ((mod as { default?: unknown }).default ?? mod) as (
          type: 0,
          ec: 'M',
        ) => { addData(s: string): void; make(): void; getModuleCount(): number; isDark(r: number, c: number): boolean };
        const qr = factory(0, 'M');
        qr.addData(value);
        qr.make();
        const count = qr.getModuleCount();
        let d = '';
        for (let row = 0; row < count; row += 1) {
          for (let col = 0; col < count; col += 1) {
            if (qr.isDark(row, col)) d += `M${col} ${row}h1v1h-1z`;
          }
        }
        if (!cancelled) setCode({ d, count });
      })
      .catch(() => {
        // No QR, but the code and the link are still on screen beside it.
        if (!cancelled) setCode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  if (!code) {
    return (
      <div
        aria-hidden="true"
        className="rounded-xs border border-line bg-surface-2"
        style={{ width: size, height: size }}
      />
    );
  }

  const span = code.count + MARGIN * 2;
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`${-MARGIN} ${-MARGIN} ${span} ${span}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="rounded-xs"
    >
      <rect x={-MARGIN} y={-MARGIN} width={span} height={span} fill="#ffffff" />
      <path d={code.d} fill="#0b0e0c" />
    </svg>
  );
}
